import React from 'react';
import { ArrowRight, TrendingUp, Brain, FileText, CheckCircle } from 'lucide-react';

const ValueBuild = ({ series }) => {
  const accentColor = series?.accentColor || '#dc2626';

  const scrollToDemo = () => {
    const demoSection = document.getElementById('clarity-demo');
    if (demoSection) {
      demoSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              This one conversation is just the start
            </h2>
            <p className="text-lg text-slate-600">
              The real value builds over time. Patterns emerge. Evidence accumulates.
            </p>
          </div>

          {/* Two column comparison */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Today */}
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200">
              <div 
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium mb-6"
                style={{ backgroundColor: `${accentColor}10`, color: accentColor }}
              >
                <CheckCircle className="w-4 h-4" />
                Today
              </div>
              
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Think through one situation</p>
                    <p className="text-sm text-slate-500">Get clarity on what's happening and how to approach it</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Get a clearer approach</p>
                    <p className="text-sm text-slate-500">Walk away with concrete next steps</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Have a thinking partner</p>
                    <p className="text-sm text-slate-500">Someone to work through it with, judgment-free</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Over Time */}
            <div 
              className="rounded-2xl p-8 border"
              style={{ 
                backgroundColor: `${accentColor}05`,
                borderColor: `${accentColor}20`
              }}
            >
              <div 
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium mb-6"
                style={{ backgroundColor: `${accentColor}15`, color: accentColor }}
              >
                <TrendingUp className="w-4 h-4" />
                Over time
              </div>
              
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <div 
                    className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ backgroundColor: `${accentColor}15` }}
                  >
                    <Brain className="w-3.5 h-3.5" style={{ color: accentColor }} />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">See patterns emerge</p>
                    <p className="text-sm text-slate-600">"You've had 4 similar situations with stakeholders this month"</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div 
                    className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ backgroundColor: `${accentColor}15` }}
                  >
                    <FileText className="w-3.5 h-3.5" style={{ color: accentColor }} />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Build a record</p>
                    <p className="text-sm text-slate-600">"Here's how you've handled similar challenges before"</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div 
                    className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ backgroundColor: `${accentColor}15` }}
                  >
                    <TrendingUp className="w-3.5 h-3.5" style={{ color: accentColor }} />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Living CV evidence</p>
                    <p className="text-sm text-slate-600">"Demonstrated stakeholder management in 12 situations"</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <button
              onClick={scrollToDemo}
              className="inline-flex items-center gap-2 px-8 py-4 text-white font-semibold rounded-xl transition-all shadow-lg hover:shadow-xl"
              style={{ backgroundColor: accentColor }}
              data-testid="value-build-cta"
            >
              Start with your first situation
              <ArrowRight className="w-5 h-5" />
            </button>
            <p className="text-sm text-slate-500 mt-4">
              Free to start. No credit card required.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ValueBuild;
